import { useEffect } from "react";
import { Typography, Row, Col, Button, Tag, Tooltip } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import ReactConfetti from "react-confetti";
import { CloseOutlined } from "@ant-design/icons";
import { FaTrophy, FaTicketAlt } from "react-icons/fa";
import { awardImagesUrl, urlHttpGov } from "../../../../../services/urls";
import { IAwardWithWinner } from "../../Steps/ImportData/useImportData";
import { IRulesGiveaway } from "../../../../../interfaces";
import { convertMoedaBRDoJSNovo } from "../../../../../utils/Conversores";
import ImagemLogo from "../../../../../assets/images/logo-lateral.png";
import useAuth from "../../../../../hooks/useAuth";

const { Text } = Typography;

interface Props {
    open: boolean;
    onClose: () => void;
    awardsWithWinners?: IAwardWithWinner[];
    rules: IRulesGiveaway | undefined;
    giveawayTitle?: string;
    cityName?: string;
}

export default function ModalWinners({
    open,
    onClose,
    awardsWithWinners = [],
    rules,
    giveawayTitle,
    cityName,
}: Props) {
    const { activeCity } = useAuth();
    const displayCityName = cityName || activeCity?.name;

    const cityLogoUrl = activeCity?.logo_county
        ? `${urlHttpGov}/logo_county/${activeCity.logo_county}`
        : activeCity?.logo_light
            ? `${urlHttpGov}/logo_county/${activeCity.logo_light}`
            : null;

    const formatCityTitle = (name?: string) => {
        if (!name) return "Programa Nota Município";
        const trimmed = name.trim();
        if (trimmed.toLowerCase().startsWith("prefeitura")) {
            return trimmed;
        }
        return `Prefeitura de ${trimmed}`;
    };

    // ESC key support
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && open) {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const validAwards = awardsWithWinners.filter(
        (a) => a && (a.winner || (a as any).winning_ticket_id || (a as any).winning_ticket_auto_id || (a as any).app_user)
    );

    return (
        <AnimatePresence>
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 9999,
                    backgroundColor: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
            >
                <ReactConfetti
                    style={{
                        zIndex: 10001,
                        position: "fixed",
                        top: 0,
                        left: 0,
                        pointerEvents: "none",
                    }}
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={800}
                    gravity={0.16}
                    tweenDuration={4500}
                    initialVelocityY={5}
                    initialVelocityX={14}
                />

                {/* Full-screen Header Bar */}
                <header
                    style={{
                        background: "#ffffff",
                        borderBottom: "1px solid #e2e8f0",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
                        padding: "12px 28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 16,
                        flexShrink: 0,
                        zIndex: 10,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        <img
                            src={ImagemLogo}
                            alt="Nota Município"
                            style={{ height: 32, objectFit: "contain" }}
                        />

                        {cityLogoUrl && (
                            <>
                                <div style={{ height: 24, width: 1, background: "#e2e8f0" }} />
                                <img
                                    src={cityLogoUrl}
                                    alt="Prefeitura"
                                    style={{ height: 30, maxWidth: 120, objectFit: "contain" }}
                                />
                            </>
                        )}

                        <div style={{ height: 24, width: 1, background: "#e2e8f0" }} />

                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                                    Ganhadores do {giveawayTitle ? `${giveawayTitle}` : ""}
                                </span>
                            </div>
                            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                                {formatCityTitle(displayCityName)}
                            </span>
                        </div>
                    </div>

                    {/* Right Controls */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Tooltip title="Fechar (Esc)">
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={onClose}
                                style={{
                                    color: "#dc2626",
                                    background: "#fee2e2",
                                    border: "1px solid #fecaca",
                                    borderRadius: 8,
                                }}
                            />
                        </Tooltip>
                    </div>
                </header>

                {/* Full-width Scrollable Content Body */}
                <main
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "24px 28px",
                    }}
                >
                    <div style={{ maxWidth: 1440, width: "100%", margin: "0 auto" }}>
                        <Row gutter={[16, 16]}>
                            {validAwards.map((award, index) => {
                                const rankNumber = index + 1;
                                const ticketId =
                                    award.winner?.winning_ticket_id ??
                                    (award as any).winning_ticket_id ??
                                    (award as any).winning_ticket_auto_id ??
                                    "000000";

                                const winnerName =
                                    award.winner?.app_user_name ??
                                    (award as any).app_user?.name ??
                                    (award as any).app_user_auto?.name ??
                                    "Contemplado(a)";

                                const institutionName =
                                    award.winner?.social_institution_name ??
                                    (award as any).social_institution_name;

                                const rankBadgeStyle =
                                    rankNumber === 1
                                        ? { background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "#ffffff" }
                                        : rankNumber === 2
                                            ? { background: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)", color: "#ffffff" }
                                            : rankNumber === 3
                                                ? { background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", color: "#ffffff" }
                                                : { background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "#ffffff" };

                                return (
                                    <Col
                                        key={award.id || index}
                                        xs={24}
                                        sm={12}
                                        lg={8}
                                        xl={validAwards.length > 4 ? 6 : 8}
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, y: 36, scale: 0.88 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 260,
                                                damping: 20,
                                                mass: 0.9,
                                                delay: 0.3 + index * 0.5,
                                            }}
                                            whileHover={{
                                                y: -4,
                                                transition: { duration: 0.2 },
                                            }}
                                            style={{ height: "100%" }}
                                        >
                                            <div
                                                style={{
                                                    background: "#ffffff",
                                                    border: "1px solid #e2e8f0",
                                                    borderRadius: 16,
                                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                                    padding: "18px 20px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "start",
                                                    gap: 12,
                                                    height: "100%",
                                                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                                }}
                                            >
                                                <div>
                                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                                                        <div
                                                            style={{
                                                                ...rankBadgeStyle,
                                                                padding: "3px 10px",
                                                                borderRadius: 6,
                                                                fontWeight: 800,
                                                                fontSize: 12,
                                                                textAlign: "center",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 4,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <FaTrophy size={11} /> {rankNumber}º Prêmio
                                                        </div>

                                                        {award.value && Number(award.value) > 0 ? (
                                                            <Tag color="green" style={{ borderRadius: 6, fontWeight: 700, fontSize: 12, margin: 0 }}>
                                                                R$ {convertMoedaBRDoJSNovo(award.value)}
                                                            </Tag>
                                                        ) : null}
                                                    </div>

                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        {award.images && award.images.length > 0 && (
                                                            <div
                                                                style={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius: 10,
                                                                    background: "#f8fafc",
                                                                    border: "1px solid #e2e8f0",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    overflow: "hidden",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <img
                                                                    src={`${awardImagesUrl}/${award.images[0].img}`}
                                                                    alt={award.description}
                                                                    style={{ width: "90%", height: "90%", objectFit: "contain" }}
                                                                />
                                                            </div>
                                                        )}

                                                        <div
                                                            style={{
                                                                fontSize: 15,
                                                                fontWeight: 700,
                                                                color: "#0f172a",
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {award.description}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    style={{
                                                        background: "#f8fafc",
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: 8,
                                                        padding: "8px 12px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                                            Número do bilhete
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: 16,
                                                                fontWeight: 800,
                                                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                                                color: "#0f172a",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 6,
                                                            }}
                                                        >
                                                            <FaTicketAlt size={12} color="#2563eb" />
                                                            {ticketId}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Winner & Institution info */}
                                                <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                                                    {rules?.show_winner_name && winnerName && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#1e293b", flexWrap: "wrap" }}>
                                                            <span>
                                                                <Text type="secondary" style={{ color: "#64748b" }}>Ganhador:</Text>{" "}
                                                                <strong>{winnerName}</strong>
                                                            </span>
                                                        </div>
                                                    )}

                                                    {rules?.show_social_institution_name && institutionName && (
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", color: "#1e293b" }}>
                                                            <span>
                                                                <Text type="secondary" style={{ color: "#64748b" }}>Instituição:</Text>{" "}
                                                                <strong>{institutionName}</strong>
                                                            </span>
                                                            {award.social_institution_value && Number(award.social_institution_value) > 0 ? (
                                                                <Tag color="green" style={{ borderRadius: 4, fontWeight: 700, fontSize: 11, margin: 0 }}>
                                                                    + R$ {convertMoedaBRDoJSNovo(award.social_institution_value)}
                                                                </Tag>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                </main>
            </div>
        </AnimatePresence>
    );
}
